import React, { createContext, useCallback, useContext, useMemo } from 'react';
import type { ViewProps } from 'react-native';

import { client } from './client';
import type { BrazeUser } from './client/braze.types';

// ────────────────────────────────────────────────────────────────────────────────

const {
  init,
  destroy,
  changeUser,
  logCustomEvent: logCustomBrazeEvent,
  logPurchase: logBrazePurchase,
} = client;

const BrazeContext = createContext<BrazeContextType | undefined>(undefined);

/**
 Braze provider component.
 *
 * @example
 * const { syncCustomer } = useBraze();
 * syncCustomer(customer)
 *
 * @see {@link https://www.braze.com/}
 */
export const BrazeProvider = (props: Pick<ViewProps, 'children'>) => {
  //
  // ─── HELPERS ────────────────────────────────────────────────────────

  const syncCustomer = useCallback((customer: BrazeUser) => {
    if (!customer.id) return;

    changeUser?.(customer);
  }, []);

  const logCustomEvent = useCallback<BrazeContextType['logCustomEvent']>(
    (eventName, eventPayload) => {
      logCustomBrazeEvent?.(eventName, eventPayload);
    },
    [],
  );

  /**
   * Record in-app purchases so that you can track your revenue over time and across revenue sources,
   * as well as segment your users by their lifetime value (NOTE: from Braze docs).
   *
   * @see {@link https://www.braze.com/docs/developer_guide/platform_integration_guides/react_native/analytics/#logging-purchases Logging purchases | React Native}
   * @see {@link https://www.braze.com/docs/developer_guide/platform_integration_guides/web/analytics/logging_purchases/ Logging purchases | Web}
   */
  const logPurchase = useCallback<BrazeContextType['logPurchase']>(
    (...params) => {
      logBrazePurchase?.(...params);
    },
    [],
  );

  // ────────────────────────────────────────────────────────────────────

  const value = useMemo(
    () => ({ init, destroy, syncCustomer, logCustomEvent, logPurchase }),
    [logCustomEvent, logPurchase, syncCustomer],
  );

  return (
    <BrazeContext.Provider value={value}>
      {props.children}
    </BrazeContext.Provider>
  );
};

/**
 * Braze client hook.
 *
 * @example
 * const { syncCustomer } = useBraze();
 * syncCustomer('USER_ID')
 *
 * @see {@link https://www.braze.com/}
 */
export const useBraze = () => {
  const context = useContext(BrazeContext);

  if (context === undefined) {
    throw new Error('useBraze must be used within a <BrazeProvider>');
  }

  return context;
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type BrazeContextType = {
  readonly init: (() => void) | undefined;
  readonly destroy: (() => void) | undefined;
  readonly syncCustomer: (user: BrazeUser) => void;
  readonly logCustomEvent: (
    eventName: string,
    eventPayload?: Record<string, unknown>,
  ) => void;
  readonly logPurchase: (
    productId: string,
    price: number,
    currencyCode?: string,
    quantity?: number,
    purchaseProperties?: Record<string, string | number | boolean | Date>,
  ) => void;
};
